// News: mobile scroll-snap slider with autoplay + JS-controlled peek
// - Autoplay only when (max-width: 744px) and no reduced-motion
// - Pauses on user interaction (touch/scroll/click), resumes after AUTOPLAY_RESUME_AFTER
// - Stops when section is not visible (page hidden / out of viewport)
// - No scrollIntoView (uses track.scrollTo so the page doesn't jump)

export default function initNewsSnap() {
  const sections = document.querySelectorAll('.news-section');
  if (!sections.length) return;

  const RMP = window.matchMedia('(prefers-reduced-motion: reduce)');
  const AUTOPLAY_DELAY = 5000;        // ms between slides
  const AUTOPLAY_RESUME_AFTER = 6000; // ms after user interaction
  const MQ = window.matchMedia('(max-width: 744px)');

  sections.forEach(sec => {
    const track = sec.querySelector('.container');
    const cards = track ? Array.from(track.querySelectorAll('.news-cards')) : [];
    const pag   = sec.querySelector('.news-section__pagination');
    if (!track || cards.length === 0 || !pag) return;

    let ro, io, onScroll, onUserInteract, onVisibility, onMQChange;
    let currentIndex = 0;
    let autoplayTimer = 0;
    let resumeTimer = 0;
    let mounted = false;

    // ---------- helpers ----------
    const getPads = () => {
      const cs = getComputedStyle(sec);
      return {
        padL: parseFloat(cs.paddingLeft) || 0,
        padR: parseFloat(cs.paddingRight) || 0
      };
    };

    const getPeek = () => {
      const v = getComputedStyle(sec).getPropertyValue('--peek').trim();
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 16;
    };

    const centerScrollLeftFor = (el) => {
      // центрируем карточку в горизонтальном контейнере
      const elCenter   = el.offsetLeft + el.clientWidth / 2;
      const targetLeft = Math.max(0, elCenter - track.clientWidth / 2);
      return targetLeft;
    };

    const scrollToIndex = (idx, smooth = true) => {
      const el = cards[idx];
      if (!el) return;
      const left = centerScrollLeftFor(el);
      try {
        track.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
      } catch {
        track.scrollLeft = left;
      }
    };

    const detectActiveIndex = () => {
      const viewportCenter = track.scrollLeft + track.clientWidth / 2;
      let best = 0, bestDist = Infinity;
      cards.forEach((el, idx) => {
        const center = el.offsetLeft + el.clientWidth / 2;
        const d = Math.abs(center - viewportCenter);
        if (d < bestDist) { best = idx; bestDist = d; }
      });
      return best;
    };

    const updateActive = () => {
      currentIndex = detectActiveIndex();
      Array.from(pag.children).forEach((d, k) => d.classList.toggle('is-active', k === currentIndex));
    };

    const goNext = () => {
      if (!mounted) return;
      const next = (currentIndex + 1) % cards.length;
      scrollToIndex(next);
    };

    // ---------- layout ----------
    const layoutMobileSlides = () => {
      if (!MQ.matches) return;

      const { padL, padR } = getPads();
      const peek = getPeek();
      const trackWidth = sec.clientWidth - padL - padR;
      const cardWidth  = Math.max(1, trackWidth - 2 * peek);

      cards.forEach(c => {
        c.style.flex = '0 0 ' + cardWidth + 'px';
        c.style.width = cardWidth + 'px';
        c.style.scrollSnapAlign = 'center';
      });

      // поля у крайних карточек: первая — от левого края, последняя — от правого
      if (cards[0]) cards[0].style.marginLeft = `calc(${padL}px + ${peek}px)`;
      if (cards[cards.length - 1]) cards[cards.length - 1].style.marginRight = `calc(${padR}px + ${peek}px)`;
    };

    const clearInlineLayout = () => {
      cards.forEach(c => {
        c.style.width = '';
        c.style.flex = '';
        c.style.scrollSnapAlign = '';
        c.style.marginLeft = '';
        c.style.marginRight = '';
      });
    };

    // ---------- pagination ----------
    const buildBullets = () => {
      pag.innerHTML = '';
      cards.forEach((_, i) => {
        const dot = document.createElement('span');
        dot.className = 'dot' + (i === 0 ? ' is-active' : '');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.appendChild(btn);
        pag.appendChild(dot);
        btn.addEventListener('click', () => {
          stopAutoplay();
          scrollToIndex(i);
          scheduleAutoplayResume();
        });
      });
    };

    // ---------- autoplay ----------
    const shouldAutoplay = () =>
        MQ.matches && !RMP.matches && !document.hidden;

    const startAutoplay = () => {
      if (!mounted) return;
      if (!shouldAutoplay()) return;
      if (autoplayTimer) return;
      autoplayTimer = window.setInterval(goNext, AUTOPLAY_DELAY);
    };

    const stopAutoplay = () => {
      if (autoplayTimer) { clearInterval(autoplayTimer); autoplayTimer = 0; }
      if (resumeTimer)   { clearTimeout(resumeTimer);    resumeTimer = 0;   }
    };

    const scheduleAutoplayResume = () => {
      if (RMP.matches) return;
      if (resumeTimer) { clearTimeout(resumeTimer); resumeTimer = 0; }
      resumeTimer = window.setTimeout(() => {
        resumeTimer = 0;
        startAutoplay();
      }, AUTOPLAY_RESUME_AFTER);
    };

    // ---------- mount / unmount ----------
    const mount = () => {
      if (mounted) return;
      mounted = true;

      layoutMobileSlides();
      buildBullets();

      onScroll = () => updateActive();
      track.addEventListener('scroll', onScroll, { passive: true });

      ro = new ResizeObserver(() => {
        layoutMobileSlides();
        updateActive();
      });
      ro.observe(sec);
      ro.observe(track);
      cards.forEach(c => ro.observe(c));

      io = new IntersectionObserver(entries => {
        const e = entries[0];
        if (!e) return;
        if (e.intersectionRatio < 0.5) {
          stopAutoplay();
        } else if (shouldAutoplay()) {
          startAutoplay();
        }
      }, { threshold: [0, 0.5, 1] });
      io.observe(sec);

      // user interactions → pause + resume later
      const pause = () => { stopAutoplay(); scheduleAutoplayResume(); };
      onUserInteract = pause;
      track.addEventListener('touchstart', pause, { passive: true });
      track.addEventListener('mousedown',   pause);
      track.addEventListener('wheel',       pause, { passive: true });
      pag.addEventListener('click',         pause);

      onVisibility = () => {
        if (document.hidden) stopAutoplay();
        else if (shouldAutoplay()) startAutoplay();
      };
      document.addEventListener('visibilitychange', onVisibility);

      onMQChange = () => {
        if (!MQ.matches) {
          stopAutoplay();
          clearInlineLayout();
        } else {
          layoutMobileSlides();
          // не скроллим тут — только обновляем активную точку
          updateActive();
          if (shouldAutoplay()) startAutoplay();
        }
      };
      if (MQ.addEventListener) MQ.addEventListener('change', onMQChange);
      else MQ.addListener(onMQChange);

      // первая активация (без автоскролла страницы)
      requestAnimationFrame(() => {
        layoutMobileSlides();
        updateActive();
        if (shouldAutoplay()) startAutoplay(); // если надо — поедем сами
      });
    };

    const unmount = () => {
      if (!mounted) return;
      mounted = false;

      stopAutoplay();

      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('touchstart', onUserInteract);
      track.removeEventListener('mousedown', onUserInteract);
      track.removeEventListener('wheel', onUserInteract);
      pag.removeEventListener('click', onUserInteract);

      document.removeEventListener('visibilitychange', onVisibility);

      if (MQ.removeEventListener) MQ.removeEventListener('change', onMQChange);
      else MQ.removeListener(onMQChange);

      if (ro) ro.disconnect();
      if (io) io.disconnect();

      clearInlineLayout();
      pag.innerHTML = '';
    };

    const reevaluate = () => {
      if (MQ.matches) mount();
      else unmount();
    };

    // kick
    reevaluate();

    window.addEventListener('resize', () => {
      if (!mounted || !MQ.matches) return;
      layoutMobileSlides();
      requestAnimationFrame(updateActive);
    });
  });
}
